import { type FC } from 'react';
import { useQuery } from 'urql';

import { AdditionalInfiniteCategoryProductGridDataDocument } from '@packages/gql/generated/shopping/AdditionalInfiniteCategoryProductGridDataDocument';
import { AdditionalInfiniteSearchResultProductGridDataDocument } from '@packages/gql/generated/shopping/AdditionalInfiniteSearchResultProductGridDataDocument';
import type { Device } from '@packages/themes/src/default';

import { useSharedClientSideGqlVariables } from '../../utils/gqlVariables';
import { useCleanPathname } from '../../hooks/useCleanPathname';
import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';
import { type ProductListVirtualPageProps } from '../ProductList/ProductList.VirtualPage.client';
import { ProductListVirtualPageMemoized } from '../ProductList/ProductList.VirtualPage.memoized';

// NOTE: due to lack of input unions, both queries have to be defined separately
/* GraphQL */ `
  query AdditionalInfiniteSearchResultProductGridData(
    $params: SearchParametersInput!
    $query: String!
  ) {
    search(params: $params, query: $query) {
      __typename
      ...ProductListVirtualPageFragment
    }
  }
`;

// NOTE: due to lack of input unions, both queries have to be defined separately
/* GraphQL */ `
  query AdditionalInfiniteCategoryProductGridData(
    $params: SearchParametersInput!
    $pathname: String!
  ) {
    category(params: $params, pathname: $pathname) {
      __typename
      ...ProductListVirtualPageFragment
    }
  }
`;

export type InfiniteProductGridPageLoaderProps = Omit<
  ProductListVirtualPageProps,
  'maskedResult'
> & {
  /** How many products should be loaded for each additional product page in the infinite grid */
  limit: number;
  /** Starting offset from where additional products should be loaded */
  offset: number;
  device: Device;
  referrer?: string;
  VirtualPageComponent?: FC<ProductListVirtualPageProps>;
};

const usePagedSharedQueryVariables = ({
  limit,
  offset,
  device,
  referrer,
}: Pick<InfiniteProductGridPageLoaderProps, 'limit' | 'offset' | 'device' | 'referrer'>) => {
  const variables = useSharedClientSideGqlVariables(device, referrer);
  return {
    ...variables,
    params: {
      ...variables.params,
      paging: {
        ...variables.params.paging,
        limit,
        offset,
      },
    },
  };
};

export const SearchResultPageInfiniteProductGridPageLoader: FC<
  InfiniteProductGridPageLoaderProps
> = ({
  limit,
  offset,
  device,
  referrer,
  VirtualPageComponent = ProductListVirtualPageMemoized,
  ...productGridProps
}) => {
  const searchquery = useOriginalSearchQuery();

  const variables = {
    ...usePagedSharedQueryVariables({ limit, offset, device, referrer }),
    query: searchquery ?? '',
  };

  const [{ data }] = useQuery({
    query: AdditionalInfiniteSearchResultProductGridDataDocument,
    variables,
  });

  if (!data) throw new Error('No data');
  if (data.search.__typename !== 'SuccessfulSearchResult') throw new Error('Invalid data');

  return <VirtualPageComponent maskedResult={data.search} {...productGridProps} />;
};

export const CategoryPageInfiniteProductGridPageLoader: FC<InfiniteProductGridPageLoaderProps> = ({
  limit,
  offset,
  device,
  referrer,
  VirtualPageComponent = ProductListVirtualPageMemoized,
  ...virtualPageProps
}) => {
  const variables = {
    ...usePagedSharedQueryVariables({ limit, offset, device, referrer }),
    pathname: useCleanPathname(),
  };

  const [{ data, fetching }] = useQuery({
    query: AdditionalInfiniteCategoryProductGridDataDocument,
    variables,
  });

  if (fetching) return null; // is suspended anyways, but otherwise the error below is thrown immediately
  if (data?.category.__typename !== 'CategoryResult') throw new Error('Invalid data');

  return <VirtualPageComponent maskedResult={data.category} {...virtualPageProps} />;
};
